/* ET - LP Gallery USPs (sections/et-lp-showcase.liquid)
   USP accordion with one item open at a time, an autoplay progress bar and an optional image swap per item.
   - Autoplay: the bar fills over the interval, then the next item opens (loops). Click resets the progress.
     Pauses on mouse hover, keyboard focus, while off-screen, in a hidden tab and while a block is selected
     in the theme editor. No autoplay for prefers-reduced-motion (bar then shows (index + 1) / count).
   - No globals: each instance is stored on its root element, so a second copy of this script
     (the section added twice) reuses the instance instead of binding twice. */
(() => {
  const ROOT_SELECTOR = '[data-et-lps]';
  const INSTANCE = '__etLpShowcase';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isFocusVisible = (el) => {
    try {
      return el.matches(':focus-visible');
    } catch (e) {
      return true;
    }
  };

  class EtLpShowcase {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-et-lps-item]'));
      this.triggers = this.items.map((item) => item.querySelector('[data-et-lps-trigger]'));
      this.panels = this.items.map((item) => item.querySelector('[data-et-lps-panel]'));
      this.fill = root.querySelector('[data-et-lps-progress]');
      this.baseLayer = root.querySelector('[data-et-lps-media="base"]');
      this.layers = new Map();
      root.querySelectorAll('[data-et-lps-media]').forEach((layer) => {
        if (layer !== this.baseLayer) this.layers.set(Number(layer.dataset.etLpsMedia), layer);
      });

      this.count = this.items.length;
      this.duration = Math.max(Number(root.dataset.interval) || 6, 1) * 1000;
      this.wantsAutoplay = root.dataset.autoplay === 'true' && this.count > 1;
      this.index = Math.max(this.items.findIndex((item) => item.classList.contains('is-open')), 0);
      this.elapsed = 0;
      this.last = 0;
      this.frame = null;
      this.pauses = new Set();
      this.lastPointer = null;

      this.tick = this.tick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);

      root.addEventListener('click', this.onClick);
      root.addEventListener('pointerdown', this.onPointerDown);
      root.addEventListener('keydown', this.onKeydown);
      root.addEventListener('pointerenter', this.onPointerEnter);
      root.addEventListener('pointerleave', this.onPointerLeave);
      root.addEventListener('focusin', this.onFocusIn);
      root.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', this.onMotionChange);
      }

      if (document.hidden) this.pauses.add('hidden');

      if ('IntersectionObserver' in window) {
        this.pauses.add('offscreen');
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.resume('offscreen');
              else this.pause('offscreen');
            });
          },
          { threshold: 0.2 }
        );
        this.observer.observe(root);
      }

      this.syncAutoplay();
    }

    get autoplay() {
      return this.wantsAutoplay && !reducedMotion.matches;
    }

    get playing() {
      return this.autoplay && this.pauses.size === 0;
    }

    syncAutoplay() {
      this.root.classList.toggle('is-autoplay', this.autoplay);
      this.renderProgress();
      this.update();
    }

    activate(index) {
      if (index < 0 || index >= this.count) return;

      this.index = index;
      this.elapsed = 0;

      this.items.forEach((item, i) => {
        const open = i === index;
        item.classList.toggle('is-open', open);
        if (this.triggers[i]) this.triggers[i].setAttribute('aria-expanded', String(open));
        if (this.panels[i]) this.panels[i].hidden = !open;
      });

      const activeLayer = this.layers.get(index) || null;
      this.layers.forEach((layer) => {
        const active = layer === activeLayer;
        layer.classList.toggle('is-active', active);
        if (active) layer.removeAttribute('aria-hidden');
        else layer.setAttribute('aria-hidden', 'true');
      });
      if (this.baseLayer) {
        if (activeLayer) this.baseLayer.setAttribute('aria-hidden', 'true');
        else this.baseLayer.removeAttribute('aria-hidden');
      }

      this.renderProgress();
    }

    renderProgress() {
      if (!this.fill || !this.count) return;

      const step = this.autoplay ? Math.min(this.elapsed / this.duration, 1) : 1;
      const value = (this.index + step) / this.count;
      this.fill.style.setProperty('--et-lps-progress', value.toFixed(4));
    }

    update() {
      if (this.playing && this.frame === null) {
        this.last = performance.now();
        this.frame = window.requestAnimationFrame(this.tick);
      } else if (!this.playing && this.frame !== null) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    tick(now) {
      this.elapsed += Math.max(now - this.last, 0);
      this.last = now;

      if (this.elapsed >= this.duration) {
        this.activate((this.index + 1) % this.count);
      } else {
        this.renderProgress();
      }

      this.frame = window.requestAnimationFrame(this.tick);
    }

    pause(reason) {
      this.pauses.add(reason);
      this.update();
    }

    resume(reason) {
      this.pauses.delete(reason);
      this.update();
    }

    onPointerDown(event) {
      this.lastPointer = event.pointerType;
    }

    onClick(event) {
      const index = this.triggers.indexOf(event.target.closest('[data-et-lps-trigger]'));
      if (index === -1) return;
      this.activate(index);

      // Touch and keyboard users have no hover pause: a deliberate choice stops autoplay (WCAG 2.2.2).
      if (this.lastPointer !== 'mouse' && this.wantsAutoplay) {
        this.wantsAutoplay = false;
        this.syncAutoplay();
      }
      this.lastPointer = null;
    }

    onKeydown(event) {
      const index = this.triggers.indexOf(event.target.closest('[data-et-lps-trigger]'));
      if (index === -1) return;

      let next;
      switch (event.key) {
        case 'ArrowDown':
          next = (index + 1) % this.count;
          break;
        case 'ArrowUp':
          next = (index - 1 + this.count) % this.count;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.count - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      if (this.triggers[next]) this.triggers[next].focus();
    }

    onPointerEnter(event) {
      if (event.pointerType === 'mouse') this.pause('hover');
    }

    onPointerLeave() {
      this.resume('hover');
    }

    onFocusIn(event) {
      if (isFocusVisible(event.target)) this.pause('focus');
    }

    onFocusOut(event) {
      if (!this.root.contains(event.relatedTarget)) this.resume('focus');
    }

    onVisibilityChange() {
      if (document.hidden) this.pause('hidden');
      else this.resume('hidden');
    }

    onMotionChange() {
      this.elapsed = 0;
      this.syncAutoplay();
    }

    selectBlock(target) {
      const index = this.items.indexOf(target.closest('[data-et-lps-item]'));
      if (index !== -1) this.activate(index);
      this.pause('editor');
    }

    deselectBlock() {
      this.resume('editor');
    }

    destroy() {
      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.observer) this.observer.disconnect();

      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('pointerdown', this.onPointerDown);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.root.removeEventListener('pointerenter', this.onPointerEnter);
      this.root.removeEventListener('pointerleave', this.onPointerLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (typeof reducedMotion.removeEventListener === 'function') {
        reducedMotion.removeEventListener('change', this.onMotionChange);
      }

      delete this.root[INSTANCE];
    }
  }

  const findRoots = (scope) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return [];
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);
    return roots;
  };

  const init = (scope) => {
    findRoots(scope).forEach((root) => {
      if (!root[INSTANCE]) root[INSTANCE] = new EtLpShowcase(root);
    });
  };

  const destroy = (scope) => {
    findRoots(scope).forEach((root) => {
      if (root[INSTANCE]) root[INSTANCE].destroy();
    });
  };

  const instanceFor = (target) => {
    const root = target instanceof Element ? target.closest(ROOT_SELECTOR) : null;
    return root ? root[INSTANCE] : null;
  };

  init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const instance = instanceFor(event.target);
    if (instance) instance.selectBlock(event.target);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const instance = instanceFor(event.target);
    if (instance) instance.deselectBlock();
  });
})();
